import { theaterPublicSlideUrls } from "../cms/content/theaterPublicSlides";

/**
 * Folder names under `src/images/Theatre_images/<FOLDER>/` → marketing titles (no file paths in UI).
 */
export const THEATER_FOLDER_DISPLAY_NAMES = {
  GALAXY: "Galaxy Theater",
  JUPITER: "Jupiter Theater",
  MERCURY: "Mercury Theater",
};

/**
 * Turn folder names like "Grand_Plex_Miyapur" into readable labels.
 */
export function formatTheaterFolderLabel(folder) {
  if (!folder || typeof folder !== "string") return "Theatre";
  return folder
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Resolved display name for a theatre folder (prefers explicit map, then formatted fallback). */
export function getTheaterDisplayName(folder) {
  const key = String(folder || "").trim().toUpperCase();
  if (THEATER_FOLDER_DISPLAY_NAMES[key]) return THEATER_FOLDER_DISPLAY_NAMES[key];
  return formatTheaterFolderLabel(folder);
}

function loadWebpackTheaterSlides() {
  const ctx = require.context(
    "../images/Theatre_images",
    true,
    /\.(jpe?g|png|webp|gif|avif)$/i
  );

  const slides = [];
  ctx.keys().forEach((key) => {
    const relative = key.replace(/^\.\//, "");
    const parts = relative.split("/");
    const folder = parts[0];
    if (!folder) return;
    slides.push({
      src: ctx(key),
      theaterFolder: folder,
      label: getTheaterDisplayName(folder),
      sortKey: `${folder}/${parts.slice(1).join("/")}`,
    });
  });

  slides.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return slides.map(({ sortKey: _s, ...rest }) => rest);
}

let webpackSlidesCache;

export function getWebpackTheaterSlides() {
  if (!webpackSlidesCache) {
    webpackSlidesCache = loadWebpackTheaterSlides();
  }
  return webpackSlidesCache;
}

/**
 * Webpack slides (from `src/images/Theatre_images/<folder>/`) plus optional public URLs.
 */
export function getAllTheaterHeroSlides() {
  const fromWebpack = getWebpackTheaterSlides();
  const fromPublic = (theaterPublicSlideUrls || []).map(({ folder, path }) => ({
    src: path,
    theaterFolder: folder,
    label: getTheaterDisplayName(folder),
  }));

  const merged = [...fromWebpack, ...fromPublic];
  merged.sort((a, b) =>
    `${a.theaterFolder}/${a.src}`.localeCompare(`${b.theaterFolder}/${b.src}`)
  );
  return merged;
}

/**
 * Slides grouped by theatre folder — for the Gallery section (per-theatre grids).
 */
export function getTheaterGalleryGroups() {
  const slides = getAllTheaterHeroSlides();
  const map = new Map();
  for (const slide of slides) {
    const key = slide.theaterFolder;
    if (!map.has(key)) {
      map.set(key, {
        folder: key,
        label: getTheaterDisplayName(key),
        images: [],
      });
    }
    map.get(key).images.push(slide.src);
  }
  return Array.from(map.values()).sort((a, b) => a.folder.localeCompare(b.folder));
}

/** Webpack sometimes returns `{ default: url }` for assets — normalize for `<img src>`. */
export function resolveTheaterImageSrc(src) {
  if (src == null) return "";
  if (typeof src === "object" && src.default != null) return src.default;
  return src;
}

/**
 * Optional `public/images/Theatre_images/gallery-manifest.json` when images live under
 * `public/` only (not bundled by webpack). Shape:
 * `{ "GALAXY": ["/images/Theatre_images/GALAXY/a.jpg", ...], "MERCURY": [...] }`
 */
export async function fetchPublicTheaterGalleryManifest() {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const url = `${base}/images/Theatre_images/gallery-manifest.json`;
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return [];
    const json = await res.json();
    if (!json || typeof json !== "object") return [];
    const groups = [];
    for (const folder of Object.keys(json)) {
      const urls = json[folder];
      if (!Array.isArray(urls)) continue;
      groups.push({
        folder,
        label: getTheaterDisplayName(folder),
        images: urls.filter(Boolean).map(String),
      });
    }
    return groups;
  } catch {
    return [];
  }
}

/** Merge webpack-backed groups with manifest groups (dedupe URLs per folder). */
export function mergeTheaterGalleryGroupLists(a, b) {
  const map = new Map();
  const add = (list) => {
    for (const g of list) {
      const raw = String(g.folder || "").trim();
      if (!raw) continue;
      const ukey = raw.toUpperCase();
      if (!map.has(ukey)) {
        map.set(ukey, {
          folder: raw,
          label: getTheaterDisplayName(raw),
          images: [],
        });
      }
      const entry = map.get(ukey);
      for (const src of g.images || []) {
        const resolved = resolveTheaterImageSrc(src);
        const u = typeof resolved === "string" ? resolved : String(resolved);
        if (u && !entry.images.includes(u)) entry.images.push(u);
      }
    }
  };
  add(a);
  add(b);
  return Array.from(map.values()).sort((x, y) =>
    x.folder.localeCompare(y.folder)
  );
}
